export type ChartMarkerSeries = "making" | "wait" | "trend";

export type ChartHitGeometry = {
  width: number;
  height: number;
  plotLeft: number;
  plotRight: number;
  plotTop: number;
  plotBottom: number;
  xPositions: number[];
  makingYPositions: number[];
  waitYPositions: number[];
  trendYPositions?: number[];
  visibleSeries?: ChartMarkerSeries[];
};

export type ChartMarkerHit = {
  index: number;
  series: ChartMarkerSeries;
  x: number;
  y: number;
};

const PLOT_EDGE_TOLERANCE = 12;

export function findNearestChartMarker(
  pointerX: number,
  pointerY: number,
  geometry: ChartHitGeometry,
): ChartMarkerHit | null {
  if (
    !Number.isFinite(pointerX) ||
    !Number.isFinite(pointerY) ||
    pointerX < geometry.plotLeft - PLOT_EDGE_TOLERANCE ||
    pointerX > geometry.plotRight + PLOT_EDGE_TOLERANCE ||
    pointerY < geometry.plotTop - PLOT_EDGE_TOLERANCE ||
    pointerY > geometry.plotBottom + PLOT_EDGE_TOLERANCE
  ) {
    return null;
  }

  const pointCount = geometry.xPositions.length;
  let nearest: (ChartMarkerHit & { distanceSquared: number }) | null = null;
  const seriesPositions: Array<{
    series: ChartMarkerSeries;
    yPositions: number[];
  }> = [
    { series: "making", yPositions: geometry.makingYPositions },
    { series: "wait", yPositions: geometry.waitYPositions },
  ];
  if (geometry.trendYPositions) {
    seriesPositions.push({ series: "trend", yPositions: geometry.trendYPositions });
  }
  const visibleSeriesPositions = seriesPositions.filter(
    ({ series }) => !geometry.visibleSeries || geometry.visibleSeries.includes(series),
  );

  for (let index = 0; index < pointCount; index += 1) {
    const x = geometry.xPositions[index];
    if (!Number.isFinite(x)) continue;

    for (const { series, yPositions } of visibleSeriesPositions) {
      const y = yPositions[index];
      if (!Number.isFinite(y)) continue;
      const distanceSquared = (x - pointerX) ** 2 + (y - pointerY) ** 2;
      const isCloser = !nearest || distanceSquared < nearest.distanceSquared;
      const isLaterVisibleMarkerAtSameDistance =
        nearest && distanceSquared === nearest.distanceSquared && index > nearest.index;

      if (isCloser || isLaterVisibleMarkerAtSameDistance) {
        nearest = { index, series, x, y, distanceSquared };
      }
    }
  }

  if (!nearest) return null;
  return {
    index: nearest.index,
    series: nearest.series,
    x: nearest.x,
    y: nearest.y,
  };
}
